import React from "react";
import { Col, Form, Row } from "react-bootstrap";
import AppSelect from "./ui/AppSelect";

/**
 * DiscountFields: the vendor discount section of a product form, shared by
 * the restaurant, retailer-wholesaler, brand and healthcare forms.
 *
 * `value` is the product's `discount` object (or null) and `onChange` receives
 * the next object. The form owns the state and sends it as `discount` on save;
 * the server validates it (services/pricing/discountService.js) and derives
 * the sale price on every customer read. `price` on the product is never
 * changed by a discount.
 */

export const EMPTY_DISCOUNT = {
  enabled: false,
  type: "PERCENT",
  value: "",
  startsAt: "",
  endsAt: "",
  label: "",
};

const toDateInput = (raw) => {
  if (!raw) return "";
  const d = raw instanceof Date ? raw : new Date(raw);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/** The stored shape, with dates as yyyy-mm-dd strings for the inputs. */
export const discountToForm = (discount) => ({
  ...EMPTY_DISCOUNT,
  ...(discount || {}),
  value: discount?.value ?? "",
  startsAt: toDateInput(discount?.startsAt),
  endsAt: toDateInput(discount?.endsAt),
  label: discount?.label || "",
});

/** What the API expects. An end date covers the whole of that day. */
export const discountToPayload = (form) => {
  if (!form) return null;
  const value = Number(form.value);
  return {
    enabled: form.enabled === true,
    type: form.type === "AMOUNT" ? "AMOUNT" : "PERCENT",
    value: Number.isFinite(value) ? value : 0,
    startsAt: form.startsAt ? new Date(`${form.startsAt}T00:00:00`).toISOString() : null,
    endsAt: form.endsAt ? new Date(`${form.endsAt}T23:59:59.999`).toISOString() : null,
    label: String(form.label || "").trim().slice(0, 40),
  };
};

/** Whatever shape the form state holds (stored or edited), as the API expects. */
export const discountPayloadOf = (state) => (state ? discountToPayload(discountToForm(state)) : undefined);

const money = (n, currency) => `${currency} ${Number(n || 0).toLocaleString()}`;

/** The customer's price under this form state, for the preview. */
export const previewSale = (form, listPrice) => {
  const list = Number(listPrice);
  const value = Number(form?.value);
  if (!form?.enabled || !(value > 0) || !(list > 0)) return null;
  const cut = form.type === "AMOUNT" ? value : (list * value) / 100;
  const sale = Math.max(0, Number.isInteger(list) ? Math.round(list - cut) : Math.round((list - cut) * 100) / 100);
  if (sale >= list) return null;
  return { sale, percentOff: Math.round(((list - sale) / list) * 100), saving: list - sale };
};

export default function DiscountFields({
  value,
  onChange,
  listPrice,
  currency = "UGX",
  idPrefix = "discount",
  switchLabel = "Discount this item",
  helpText = "Shown on the app, the web, WhatsApp and the terminal with the old price struck through. The list price above is not changed. An item discount wins over a store-wide offer; the two are never added together.",
  showPreview = true,
  // A disabled fieldset switches off every control inside, so a login without
  // pricing:edit sees the discount but cannot change it.
  disabled = false,
  title,
}) {
  const form = discountToForm(value);
  const set = (patch) => onChange({ ...form, ...patch });
  const preview = previewSale(form, listPrice);
  const on = form.enabled === true;
  const percentTooHigh = form.type === "PERCENT" && Number(form.value) > 90;
  const amountTooHigh =
    form.type === "AMOUNT" && Number(listPrice) > 0 && Number(form.value) >= Number(listPrice);
  const datesInverted = form.startsAt && form.endsAt && form.endsAt < form.startsAt;

  return (
    <fieldset disabled={disabled} title={disabled ? title : undefined} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
    <div className={`lsk-discount${on ? " is-on" : ""}`}>
      <div className="lsk-discount__head">
        <div>
          <div className="lsk-discount__titleRow">
            <span className="lsk-discount__title">{switchLabel}</span>
            <span className={`lsk-discount__state${on ? " is-on" : ""}`}>{on ? "On" : "Off"}</span>
          </div>
          <p className="lsk-discount__help">{helpText}</p>
        </div>
        <Form.Check
          type="switch"
          className="lsk-discount__toggle"
          id={`${idPrefix}-enabled`}
          checked={on}
          onChange={(e) => set({ enabled: e.target.checked })}
          aria-label={switchLabel}
        />
      </div>

      {on ? (
        <div className="lsk-discount__body">
          {showPreview ? (
            <div className="lsk-discount__preview">
              <div className="lsk-discount__previewLabel">Customers will see</div>
              {preview ? (
                <div className="lsk-discount__previewRow">
                  <span className="lsk-discount__badge">{preview.percentOff}% OFF</span>
                  <span className="lsk-discount__was">{money(listPrice, currency)}</span>
                  <span className="lsk-discount__now">{money(preview.sale, currency)}</span>
                  <span className="lsk-discount__saving">
                    (they save {money(preview.saving, currency)})
                  </span>
                </div>
              ) : (
                <div className="lsk-discount__saving" style={{ marginTop: 6 }}>
                  Enter the list price and a discount value to preview the sale price.
                </div>
              )}
              <p className="lsk-discount__note">
                Lusuku&apos;s commission on marketplace orders is charged on the sale price, so you
                fund the discount. Counter sales carry no commission.
              </p>
            </div>
          ) : null}

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3" controlId={`${idPrefix}-type`}>
                <Form.Label>Type</Form.Label>
                <AppSelect block value={form.type} onChange={(e) => set({ type: e.target.value })}>
                  <option value="PERCENT">Percentage off</option>
                  <option value="AMOUNT">Amount off ({currency})</option>
                </AppSelect>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3" controlId={`${idPrefix}-value`}>
                <Form.Label>
                  {form.type === "AMOUNT" ? `Amount off (${currency})` : "Percent off"}
                </Form.Label>
                <Form.Control
                  type="number"
                  min="1"
                  max={form.type === "PERCENT" ? "90" : undefined}
                  step={form.type === "PERCENT" ? "1" : "any"}
                  value={form.value}
                  onChange={(e) => set({ value: e.target.value })}
                  isInvalid={percentTooHigh || amountTooHigh}
                />
                <Form.Control.Feedback type="invalid">
                  {percentTooHigh
                    ? "A percentage discount cannot exceed 90%."
                    : `An amount off of ${money(form.value, currency)} would make this item free. Keep it below the ${money(listPrice, currency)} list price.`}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3" controlId={`${idPrefix}-starts`}>
                <Form.Label>Starts (optional)</Form.Label>
                <Form.Control
                  type="date"
                  value={form.startsAt}
                  onChange={(e) => set({ startsAt: e.target.value })}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3" controlId={`${idPrefix}-ends`}>
                <Form.Label>Ends (optional)</Form.Label>
                <Form.Control
                  type="date"
                  value={form.endsAt}
                  onChange={(e) => set({ endsAt: e.target.value })}
                  isInvalid={!!datesInverted}
                />
                <Form.Control.Feedback type="invalid">
                  The end date must be after the start date.
                </Form.Control.Feedback>
                <Form.Text className="text-muted">
                  The offer runs to the end of this day and switches itself off.
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3" controlId={`${idPrefix}-label`}>
            <Form.Label>Label (optional)</Form.Label>
            <Form.Control
              type="text"
              maxLength={40}
              placeholder="Lunch special"
              value={form.label}
              onChange={(e) => set({ label: e.target.value })}
            />
          </Form.Group>
        </div>
      ) : null}
    </div>
    </fieldset>
  );
}
